import { useEffect, useRef, useState } from 'react';
import { Activity, ChevronDown, ChevronUp, GripHorizontal, Maximize2, X } from 'lucide-react';
import { ADVENTURE_LOG_FILTERS, filteredAdventureEntries } from '../ui/adventureLogModel.js';
import './AdventureLogDock.css';
const HEIGHT_KEY = 'the-dope-man.adventure-log-dock-height-v1';

function initialHeight() {
  if (typeof window === 'undefined') return 220;
  const stored = Number(window.localStorage.getItem(HEIGHT_KEY));
  return Number.isFinite(stored) && stored >= 150 && stored <= 620 ? stored : 220;
}

export function AdventureLogDock({ gameState, enabled = true, panel = 'compact', filter = 'all', unread = 0, onPanelChange, onFilterChange, onOpenFull }) {
  const scrollRef = useRef(null);
  const latestRef = useRef('');
  const [height, setHeight] = useState(initialHeight);
  const [awayFromNewest, setAwayFromNewest] = useState(false);
  const [newWhileReading, setNewWhileReading] = useState(0);
  const entries = filteredAdventureEntries(gameState, { filter, showRoutine: true });
  const visible = panel === 'expanded' ? entries.slice(0, 36) : entries.slice(0, 8);
  const newestId = entries[0]?.id || '';

  useEffect(() => {
    if (!newestId) return;
    if (!latestRef.current) { latestRef.current = newestId; return; }
    if (newestId !== latestRef.current) {
      if (panel !== 'hidden' && awayFromNewest) setNewWhileReading((count) => Math.min(99, count + 1));
      else if (panel !== 'hidden') scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
      latestRef.current = newestId;
    }
  }, [newestId, panel, awayFromNewest]);

  useEffect(() => {
    if (panel === 'hidden') return;
    setNewWhileReading(0);
    setAwayFromNewest(false);
    scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [filter]);

  if (!enabled) return null;
  if (panel === 'hidden') {
    return <button className="adventure-log-peek" onClick={() => onPanelChange?.('compact')} aria-label="Show Adventure Log">
      <Activity size={17} /><span>Adventure Log</span>{unread > 0 && <i>{Math.min(99, unread)}</i>}<ChevronUp size={16} />
    </button>;
  }

  const startResize = (event) => {
    if (typeof window === 'undefined' || window.matchMedia('(max-width: 820px)').matches) return;
    event.preventDefault();
    let lastHeight = height;
    const onMove = (moveEvent) => {
      const next = Math.max(150, Math.min(620, window.innerHeight - moveEvent.clientY - 10));
      lastHeight = next;
      setHeight(next);
    };
    const onEnd = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onEnd);
      window.localStorage.setItem(HEIGHT_KEY, String(Math.round(lastHeight)));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd, { once: true });
  };

  const resizeBy = (delta) => {
    const next = Math.max(150, Math.min(620, height + delta));
    setHeight(next);
    if (typeof window !== 'undefined') window.localStorage.setItem(HEIGHT_KEY, String(Math.round(next)));
  };

  const jumpToNewest = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    setAwayFromNewest(false);
    setNewWhileReading(0);
  };

  return <aside className={`adventure-log-dock ${panel}`} aria-label="Adventure Log" style={{ '--adventure-log-height': `${height}px` }}>
    <button className="adventure-log-resize" type="button" aria-label="Resize Adventure Log" role="separator" aria-orientation="horizontal" onPointerDown={startResize} onKeyDown={(event) => { if (event.key === 'ArrowUp') resizeBy(24); if (event.key === 'ArrowDown') resizeBy(-24); }}><GripHorizontal size={22} /></button>
    <header>
      <div><span>ADVENTURE LOG</span><strong>{panel === 'expanded' ? 'Run history' : 'Recent trouble'}</strong></div>
      <div className="adventure-dock-actions">
        {onOpenFull && <button onClick={onOpenFull} title="Open full Adventure Log"><Maximize2 size={16} /></button>}
        <button onClick={() => onPanelChange?.(panel === 'expanded' ? 'compact' : 'expanded')} title={panel === 'expanded' ? 'Compact' : 'Expand'}>{panel === 'expanded' ? <ChevronDown size={17} /> : <ChevronUp size={17} />}</button>
        <button onClick={() => onPanelChange?.('hidden')} title="Hide"><X size={17} /></button>
      </div>
    </header>
    <nav>{ADVENTURE_LOG_FILTERS.map(([id,label]) => <button key={id} className={filter === id ? 'active' : ''} onClick={() => onFilterChange?.(id)}>{label}</button>)}</nav>
    {newWhileReading > 0 && <button className="adventure-log-new" onClick={jumpToNewest}>{newWhileReading} new {newWhileReading === 1 ? 'entry' : 'entries'} ↑</button>}
    <div ref={scrollRef} className="adventure-log-scroll" onScroll={(event) => { const away = event.currentTarget.scrollTop > 24; setAwayFromNewest(away); if (!away) setNewWhileReading(0); }}>
      {visible.length ? visible.map((entry) => <article key={entry.id} className={`log-${entry.category} ${entry.important ? 'important' : ''}`}>
        <span>{entry.category.toUpperCase()} · DAY {entry.day}{entry.period ? ` · ${entry.period}` : ''}</span>
        <strong>{entry.title}</strong>
        <p>{entry.message}</p>
      </article>) : <p className="adventure-log-empty">Nothing worth writing down yet. Suspicious.</p>}
    </div>
  </aside>;
}
